import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCProjectApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-project.api-model';
import {CpmCProjectApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-project.api-service';
import {
    CpmCProjectCreateApiSchema,
    CpmCProjectUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-project.api-schemas';
import {CustomerUrlBuilderUtil} from '../../utils/customer-url-builder.util';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

export class NewEditProjectModalConfig {
    constructor(public existing?: CpmCProjectApiModel) {}
}

export class NewEditProjectModalResult {
    constructor(public item?: CpmCProjectApiModel) {}
}

/**
 * New/Edit Project modal
 */
@Component({
    selector: 'app-new-edit-project-modal',
    templateUrl: './new-edit-project-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditProjectModalComponent extends IBaseCRUModalComponent<
    CpmCProjectApiModel,
    NewEditProjectModalComponent,
    NewEditProjectModalConfig,
    NewEditProjectModalResult
> {
    protected item?: CpmCProjectApiModel;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.goTo = () => {
            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate([
                CustomerUrlBuilderUtil.customerProject(customerUUID!, this.item?.uuid)
            ]);
        };

        this.configure('ProjectApiModel', (model: CpmCProjectApiModel) => model.name ?? '<Unknown project>');
        this.config.width = '30rem';
        this.contentMinHeight = '28rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            name: new FormControl<string | null>(null, [Validators.required]),
            description: new FormControl<string | null>(null, [])
        });

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading the project...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;

                    this.item = await firstValueFrom(
                        CpmCProjectApiService.getProjectByUUID(this.config.data?.extended?.existing?.uuid ?? '')
                    );
                }
            }
        ]);

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the project...';
            this.form.patchValue(this.item!);
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmit: CpmCProjectCreateApiSchema | CpmCProjectUpdateApiSchema = this.isCreationMode
            ? {
                  name: this.form.controls['name'].value,
                  description: this.form.controls['description'].value
              }
            : {
                  name: this.form.controls['name'].value,
                  description: this.form.controls['description'].value
              };

        await this.submitCU(
            null,
            CpmCProjectApiService.createProject,
            CpmCProjectApiService.updateProject,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCProjectApiModel) => new NewEditProjectModalResult(resultCRUD)
        );
    }
}
